import type { OrderSort, OrderStatus, OrderView, PaymentFilter } from '../types';

/** Набор фильтров списка заказов. Пустые поля в запрос не уходят. */
export interface OrderFilters {
  query: string;
  view: OrderView | '';
  status: OrderStatus | '';
  payment: PaymentFilter | '';
  tag: string;
  deadlineFrom: string;
  deadlineTo: string;
  sort: OrderSort;
  archived: boolean;
}

export const EMPTY_ORDER_FILTERS: OrderFilters = {
  query: '',
  view: '',
  status: '',
  payment: '',
  tag: '',
  deadlineFrom: '',
  deadlineTo: '',
  sort: 'created',
  archived: false,
};

export const ORDER_VIEW_LABELS: Record<OrderView, string> = {
  waitingArtist: 'Ждут меня',
  waitingClient: 'Ждут клиента',
  unpaid: 'Не оплачены',
  thisWeek: 'На этой неделе',
  overdue: 'Просрочены',
};

export const ORDER_SORT_LABELS: Record<OrderSort, string> = {
  created: 'Сначала новые',
  deadline: 'По дедлайну',
  priority: 'По приоритету',
  queue: 'По очереди',
  updated: 'По обновлению',
};

export const PAYMENT_FILTER_LABELS: Record<PaymentFilter, string> = {
  UNPAID: 'Не оплачен',
  PARTIALLY_PAID: 'Частично',
  PAID: 'Оплачен',
};

const STORAGE_KEY = 'artflow_order_filters';

/**
 * Набор фильтров переживает перезапуск приложения: художник возвращается к той
 * же выборке, с которой ушёл. Хранится локально, потому что это предпочтение
 * устройства, а не данные заказа.
 */
export function loadOrderFilters(): OrderFilters {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_ORDER_FILTERS;

    // Ключи из старой версии игнорируем: берём только известные поля.
    const stored = JSON.parse(raw) as Partial<OrderFilters>;
    return { ...EMPTY_ORDER_FILTERS, ...pickKnown(stored) };
  } catch {
    return EMPTY_ORDER_FILTERS;
  }
}

export function saveOrderFilters(filters: OrderFilters): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // Приватный режим и переполненное хранилище не должны ломать список.
  }
}

export function orderFiltersQuery(filters: OrderFilters, limit: number, offset: number): string {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });

  if (filters.query.trim()) params.set('query', filters.query.trim());
  if (filters.view) params.set('view', filters.view);
  if (filters.status) params.set('status', filters.status);
  if (filters.payment) params.set('payment', filters.payment);
  if (filters.tag.trim()) params.set('tag', filters.tag.trim().toLowerCase());
  if (filters.deadlineFrom) params.set('deadlineFrom', filters.deadlineFrom);
  if (filters.deadlineTo) params.set('deadlineTo', filters.deadlineTo);
  if (filters.archived) params.set('archived', 'true');
  params.set('sort', filters.sort);

  return params.toString();
}

/** Сколько условий сузило выборку: по этому числу подписывается кнопка фильтров. */
export function activeFilterCount(filters: OrderFilters): number {
  return [
    filters.status,
    filters.payment,
    filters.tag.trim(),
    filters.deadlineFrom,
    filters.deadlineTo,
    filters.archived ? 'archived' : '',
  ].filter(Boolean).length;
}

function pickKnown(stored: Partial<OrderFilters>): Partial<OrderFilters> {
  const known: Partial<OrderFilters> = {};

  for (const key of Object.keys(EMPTY_ORDER_FILTERS) as (keyof OrderFilters)[]) {
    const value = stored[key];
    if (typeof value === typeof EMPTY_ORDER_FILTERS[key]) {
      Object.assign(known, { [key]: value });
    }
  }

  return known;
}
